import type { BuildingGeometry, BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { MaterialTag } from "building-types/geometry/faceIteratorTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { PropAttributeTable } from "building-types/props/propTypes";
import { GlazingColor } from "building-types/settings/windowTypesEnum";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WindowUnit, WindowShutters } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { DM_X_FrostedGlass } from "common/materials/library/designMaterialPath";
import type { MaterialSrc } from "common/materials/materialSrc";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { materialSrcFromPalette } from "doc/materialLookup";
import type { Face } from "geom/brep";
import { faceGroupNew } from "geom/brep";
import { extrusionToBRep } from "geom/extrude";
import { geomToFaces } from "geom/geom";
import { UVGeneration } from "geom/uv";
import {
  buildExteriorLayers,
  emitBuildingProp,
  getInteriorGroupsGeometries,
  getExteriorGroupsGeometries,
} from "./buildingGeometryTools";
import { refractionProbeSettingsForRoom } from "./refractionProbeSettingsForRoom";

interface BuildingForWindowUnitGeometries {
  readonly interiorPalette: InteriorPalette;
  readonly exteriorPalette: ExteriorPalette;
  readonly propCatalog?: PropAttributeTable;
}

interface BuildingForWindowGeometries extends BuildingForWindowUnitGeometries {
  readonly windowUnits: Table<WindowUnit>;
  readonly windowShutters?: Table<WindowShutters>;
}

function appendFacesByExportComponentName(
  facesByExportComponentName: Map<string, Face[]>,
  faces: Face[],
  exportComponentNames: string[],
): void {
  for (let i = 0; i < faces.length; i++) {
    const exportComponentName = exportComponentNames[i] ?? "Glass";
    const componentFaces = facesByExportComponentName.get(exportComponentName) ?? [];
    componentFaces.push(faces[i]);
    facesByExportComponentName.set(exportComponentName, componentFaces);
  }
}

export const allWindowFaceCategories = new Set([
  FaceCategory.WINDOW_INTERIOR,
  FaceCategory.WINDOW_EXTERIOR,
  FaceCategory.WINDOW_GRID,
  FaceCategory.WINDOW_DECORATION,
  FaceCategory.WINDOW_GLASS,
  FaceCategory.WINDOW_SCREEN,
]);

/**
 * Generate BuildingGeometry for a single WindowUnit.
 */
function getWindowUnitGeometries(
  building: BuildingForWindowUnitGeometries,
  windowUnit: WindowUnit,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
  refractionProbesForFrostedGlass: boolean = false,
): void {
  const { interiorPalette, exteriorPalette } = building;
  const {
    interior,
    exterior,
    grids,
    decorations,
    glazingColor,
    glassTowardExterior,
    glassTowardInterior,
    sideFaces,
    screen,
    windowPlacement,
    interiorRoom,
    exteriorRoom,
    entityHandleForSelectingReportingUnit,
  } = windowUnit;
  const { pickHandle } = windowUnit;

  const levelId = windowPlacement.frontRoom?.level.id;

  // Handle interior groups
  if (entityTypes.has(FaceCategory.WINDOW_INTERIOR)) {
    getInteriorGroupsGeometries({
      levelId,
      groups: [interior],
      palette: interiorPalette,
      room: interiorRoom,
      faceCategory: FaceCategory.WINDOW_INTERIOR,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      emitter,
    });
  }

  // Handle exterior groups
  if (entityTypes.has(FaceCategory.WINDOW_EXTERIOR)) {
    getExteriorGroupsGeometries({
      levelId,
      groups: [exterior],
      palette: exteriorPalette,
      faceCategory: FaceCategory.WINDOW_EXTERIOR,
      setToExteriorLayer: true,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      viewEntityType: ViewEntityType.WINDOW_EXTERIOR_TRIM,
      emitter,
    });
  }

  // Handle grids
  if (entityTypes.has(FaceCategory.WINDOW_GRID)) {
    getExteriorGroupsGeometries({
      levelId,
      groups: [grids],
      palette: exteriorPalette,
      faceCategory: FaceCategory.WINDOW_GRID,
      setToExteriorLayer: true,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      viewEntityType: ViewEntityType.WINDOW_EXTERIOR_TRIM,
      emitter,
    });
  }

  // Handle decorations
  if (entityTypes.has(FaceCategory.WINDOW_DECORATION)) {
    const { geom: decorationGeom, tags: decorationTags } = decorations;
    for (let i = 0, l = decorationGeom.length; i < l; i++) {
      const { exteriorMaterial, uvGen, viewEntityDescriptor, materialTransformation, exportComponentName } =
        decorationTags[i];
      const geo = decorationGeom[i];

      if (!exteriorMaterial) {
        continue;
      }

      const materialSrc = materialSrcFromPalette(exteriorPalette, exteriorMaterial);
      assertIsNotUndefined(materialSrc);

      const faces = geomToFaces(geo);

      emitter.emit({
        geom: faceGroupNew(faces),
        faceCategory: FaceCategory.WINDOW_DECORATION,
        levelId,
        pickHandle,
        entityId: viewEntityDescriptor?.entityId,
        entityHandleForSelectingReportingUnit,
        descriptor: viewEntityDescriptor,
        exportComponentName,
        materialSlot: exteriorMaterial,
        materialSrc,
        uvGen,
        materialTransformation,
        threeLayers: buildExteriorLayers(),
      });
    }
  }

  // Handle glass
  if (entityTypes.has(FaceCategory.WINDOW_GLASS)) {
    let materialSlot: MaterialTag = "glass0";
    let glassMaterialSrc: MaterialSrc | undefined;
    if (glazingColor === GlazingColor.FROSTED && refractionProbesForFrostedGlass) {
      materialSlot = "frostedglass";
      glassMaterialSrc = DM_X_FrostedGlass.Default;
    } else {
      glassMaterialSrc = materialSrcFromPalette(exteriorPalette, materialSlot);
    }
    assertIsNotUndefined(glassMaterialSrc);

    const glassTowardExteriorFacesByComponentName = new Map<string, Face[]>();
    appendFacesByExportComponentName(
      glassTowardExteriorFacesByComponentName,
      glassTowardExterior.faces,
      glassTowardExterior.exportComponentNames,
    );
    appendFacesByExportComponentName(
      glassTowardExteriorFacesByComponentName,
      sideFaces,
      windowUnit.sideFaceExportComponentNames,
    );

    const glassTowardInteriorFacesByComponentName = new Map<string, Face[]>();
    appendFacesByExportComponentName(
      glassTowardInteriorFacesByComponentName,
      glassTowardInterior.faces,
      glassTowardInterior.exportComponentNames,
    );

    const commonGeomProperties: Pick<
      BuildingGeometry,
      | "faceCategory"
      | "levelId"
      | "pickHandle"
      | "entityHandleForSelectingReportingUnit"
      | "materialSlot"
      | "materialSrc"
      | "uvGen"
    > = {
      faceCategory: FaceCategory.WINDOW_GLASS,
      levelId,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      materialSlot,
      materialSrc: glassMaterialSrc,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
    };

    for (const [exportComponentName, faces] of glassTowardExteriorFacesByComponentName) {
      emitter.emit({
        geom: faceGroupNew(faces),
        ...commonGeomProperties,
        exportComponentName,
        ...refractionProbeSettingsForRoom(exteriorRoom, true),
      });
    }

    for (const [exportComponentName, faces] of glassTowardInteriorFacesByComponentName) {
      emitter.emit({
        geom: faceGroupNew(faces),
        ...commonGeomProperties,
        exportComponentName,
        ...refractionProbeSettingsForRoom(interiorRoom, false),
      });
    }
  }

  // Handle screen
  if (entityTypes.has(FaceCategory.WINDOW_SCREEN)) {
    const screenFacesByComponentName = new Map<string, Face[]>();
    for (let i = 0; i < screen.length; i++) {
      const exportComponentName = windowUnit.screenExportComponentNames[i] ?? "Screen";
      const componentFaces = screenFacesByComponentName.get(exportComponentName) ?? [];
      componentFaces.push(...extrusionToBRep(screen[i]).faces);
      screenFacesByComponentName.set(exportComponentName, componentFaces);
    }

    if (screenFacesByComponentName.size > 0) {
      const screenMaterial = materialSrcFromPalette(exteriorPalette, "glass2");
      assertIsNotUndefined(screenMaterial);

      for (const [exportComponentName, faces] of screenFacesByComponentName) {
        emitter.emit({
          geom: faceGroupNew(faces),
          faceCategory: FaceCategory.WINDOW_SCREEN,
          levelId,
          pickHandle,
          entityHandleForSelectingReportingUnit,
          exportComponentName,
          materialSlot: "glass2",
          materialSrc: screenMaterial,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          threeLayers: buildExteriorLayers(),
        });
      }
    }
  }

  // Emit props if prop catalog available (shutters, decorative elements, etc.)
  if (building.propCatalog && windowUnit.props) {
    const { propCatalog } = building;

    for (const prop of windowUnit.props) {
      emitBuildingProp(emitter, prop, {
        threeLayers: buildExteriorLayers(),
        faceCategory: FaceCategory.WINDOW_DECORATION,
        propCatalog,
        levelId,
        pickHandle,
        entityHandleForSelectingReportingUnit,
      });
    }
  }
}

/**
 * Generate BuildingGeometry for all WindowUnits.
 */
export function getWindowGeometries(
  building: BuildingForWindowGeometries,
  entityTypes: Set<FaceCategory>,
  emitter: BuildingGeometryEmitter,
  refractionProbesForFrostedGlass: boolean = false,
): void {
  const { windowUnits, exteriorPalette } = building;
  const windowShutters = building.windowShutters ?? {};
  const windowShuttersByWindowId = new Map<string, WindowShutters>();
  for (const shutterId in windowShutters) {
    const windowShutter = windowShutters[shutterId];
    windowShuttersByWindowId.set(windowShutter.window.id, windowShutter);
  }

  for (const windowId in windowUnits) {
    const windowUnit = windowUnits[windowId];
    const windowShutter = windowShuttersByWindowId.get(windowUnit.windowPlacement.id);
    emitter.beginGroup?.("Window");
    getWindowUnitGeometries(building, windowUnit, entityTypes, emitter, refractionProbesForFrostedGlass);
    if (entityTypes.has(FaceCategory.WINDOW_DECORATION) && windowShutter) {
      getWindowShutterGeometries(windowShutter, exteriorPalette, emitter);
    }
    emitter.endGroup?.("Window");
  }
}

/**
 * Generate BuildingGeometry for WindowShutters decorations.
 */
interface BuildingForWindowShuttersGeometries {
  readonly windowShutters: Table<WindowShutters>;
  readonly windowUnits?: Table<WindowUnit>;
  readonly exteriorPalette: ExteriorPalette;
}

function getWindowShutterGeometries(
  shutter: WindowShutters,
  exteriorPalette: ExteriorPalette,
  emitter: BuildingGeometryEmitter,
): void {
  const { decorations, window: windowPlacement, pickHandle } = shutter;
  const levelId = windowPlacement.frontRoom?.level.id ?? windowPlacement.backRoom?.level.id;

  const entityHandleForSelectingReportingUnit =
    roomEntityHandleForSelectingReportingUnit(windowPlacement.frontRoom) ??
    roomEntityHandleForSelectingReportingUnit(windowPlacement.backRoom);

  const { geom: decorationGeom, tags: decorationTags } = decorations;
  for (let i = 0, l = decorationGeom.length; i < l; i++) {
    const { exteriorMaterial, uvGen, viewEntityDescriptor, materialTransformation } = decorationTags[i];
    const geo = decorationGeom[i];

    if (!exteriorMaterial) {
      continue;
    }

    const materialSrc = materialSrcFromPalette(exteriorPalette, exteriorMaterial);
    assertIsNotUndefined(materialSrc);

    const faces = geomToFaces(geo);

    emitter.emit({
      geom: faceGroupNew(faces),
      faceCategory: FaceCategory.WINDOW_DECORATION,
      levelId,
      entityId: viewEntityDescriptor?.entityId,
      entityHandleForSelectingReportingUnit,
      pickHandle,
      descriptor: viewEntityDescriptor,
      exportComponentName: "Shutters",
      materialSlot: exteriorMaterial,
      materialSrc,
      uvGen,
      materialTransformation,
      threeLayers: buildExteriorLayers(),
      participatesInCrossSection: false,
    });
  }
}

export function getWindowShuttersGeometries(
  building: BuildingForWindowShuttersGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { windowShutters, windowUnits, exteriorPalette } = building;
  for (const shutterId in windowShutters) {
    const windowShutter = windowShutters[shutterId];
    if (windowUnits?.[windowShutter.window.id]) {
      continue;
    }

    getWindowShutterGeometries(windowShutter, exteriorPalette, emitter);
  }
}
